/* eslint-disable react/no-unstable-nested-components, simple-import-sort/imports */
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '~/shadcn/components/ui/Button';
import { Input } from '~/shadcn/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import { Textarea } from '~/shadcn/components/ui/Textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/shadcn/components/ui/tooltip';

import {
    FALLBACK_TEXT,
    formatNumber,
    normalizeOptionalText,
} from '~/components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import {
    computePackagingItemTotalCost,
    formatPackagingCurrency,
} from '~/components/packaging/components/packagingPOV2/utils/packagingPOValidation';

/**
 * Displays a table of packaging purchase order items in inline edit mode.
 * Column order and styling match PackagingPOCreateItemsCard.
 *
 * @param {Object} props
 * @param {Object[]} props.rows - Array of packaging PO item objects.
 * @param {Object} props.itemEditStateRef - Ref storing uncontrolled input values keyed by row.
 * @param {Set<string|number>} props.removedItemIds - Set of PO map IDs marked for removal.
 * @param {(updater: Set<string|number>|((prev: Set<string|number>) => Set<string|number>)) => void} props.setRemovedItemIds - Setter for removedItemIds.
 * @param {() => void} props.onItemEditsChange - Callback invoked when any item edit changes.
 * @param {(rowId: string) => void} [props.onRemoveNewRow] - Callback for removing a newly added row (id.startsWith('new-')).
 * @returns {JSX.Element} Packaging purchase order item edit table.
 */
export function PackagingPurchaseOrderItemEditTable({
    itemEditStateRef,
    onItemEditsChange,
    onRemoveNewRow,
    removedItemIds,
    rows,
    setRemovedItemIds,
}) {
    const [, setLocalEditsVersion] = useState(0);
    const removedItemIdsRef = useRef(removedItemIds);

    useEffect(() => {
        removedItemIdsRef.current = removedItemIds;
    }, [removedItemIds]);

    const setInputValue = useCallback(
        (rowId, field, value) => {
            const state = itemEditStateRef?.current;
            if (!state) return;
            if (!state.inputs) state.inputs = {};
            if (!state.inputs[rowId]) state.inputs[rowId] = {};
            state.inputs[rowId][field] = value;
            setLocalEditsVersion((v) => v + 1);
            if (typeof onItemEditsChange === 'function') onItemEditsChange();
        },
        [itemEditStateRef, onItemEditsChange],
    );

    const getDraftValue = useCallback(
        (rowId, field, fallback) => {
            const raw = itemEditStateRef?.current?.inputs?.[rowId]?.[field];
            if (raw === undefined) return fallback;
            if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, 'value')) {
                return raw.value;
            }
            return raw;
        },
        [itemEditStateRef],
    );

    const toggleRemove = useCallback(
        (rowId) => {
            setRemovedItemIds((prev) => {
                const next = new Set(prev);
                const normalizedId = String(rowId);
                if (next.has(normalizedId)) next.delete(normalizedId);
                else next.add(normalizedId);
                removedItemIdsRef.current = next;
                return next;
            });
        },
        [setRemovedItemIds],
    );

    const columnDefs = useMemo(() => {
        const columns = [
            {
                id: 'masterSku',
                header: 'Item Master SKU',
                accessorFn: (row) => row?.masterSku ?? row?.sku ?? null,
                cell: ({ getValue, row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    return (
                        <span className={isRemoved ? 'line-through opacity-60' : ''}>
                            {normalizeOptionalText(getValue())}
                        </span>
                    );
                },
            },
            {
                id: 'description',
                header: 'Description',
                accessorFn: (row) => row?.description ?? row?.itemName ?? null,
                cell: ({ getValue, row }) => {
                    const rowId = String(row.original.id);
                    const value = normalizeOptionalText(getValue());
                    const isFallback = value === FALLBACK_TEXT;
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const cellClassName = isRemoved ? 'line-through opacity-60' : '';

                    if (isFallback) {
                        return <span className={cellClassName}>{value}</span>;
                    }

                    return (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className={`block max-w-[240px] truncate text-left ${cellClassName}`}>
                                    {value}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{value}</TooltipContent>
                        </Tooltip>
                    );
                },
            },
            {
                id: 'qty',
                header: () => (
                    <>
                        Quantity (cases) <span className="text-destructive">*</span>
                    </>
                ),
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.qty;
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            min="1"
                            aria-label="Row qty"
                            className="w-24"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'qty', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'unitPrice',
                header: () => (
                    <>
                        Unit Price <span className="text-destructive">*</span>
                    </>
                ),
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.unitPrice;
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            inputMode="decimal"
                            step="0.001"
                            min="0.001"
                            aria-label="Row unit price"
                            className="w-24"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'unitPrice', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'numPallets',
                header: () => (
                    <>
                        Pallets per SKU <span className="text-destructive">*</span>
                    </>
                ),
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.numPallets;
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            min="1"
                            aria-label="Row pallets per sku"
                            className="w-24"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'numPallets', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'unitsPerCase',
                header: () => (
                    <>
                        Units per Case <span className="text-destructive">*</span>
                    </>
                ),
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.unitsPerCase;
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            min="1"
                            aria-label="Row units per case"
                            className="w-24"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'unitsPerCase', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'vendorSku',
                header: 'Vendor SKU',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = row.original?.vendorSku ?? '';
                    return (
                        <Input
                            type="text"
                            aria-label="Row vendor sku"
                            className="w-32"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'vendorSku', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'notes',
                header: 'Notes',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = row.original?.notes ?? '';
                    return (
                        <Textarea
                            aria-label="Row notes"
                            className="min-h-[32px] max-w-[240px]"
                            rows={2}
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'notes', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'totalUnits',
                header: 'Total Units',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const qty = Number(getDraftValue(rowId, 'qty', row.original?.qty));
                    const unitsPerCase = Number(getDraftValue(rowId, 'unitsPerCase', row.original?.unitsPerCase));
                    if (qty && unitsPerCase) {
                        return formatNumber(Number(qty) * Number(unitsPerCase));
                    }
                    return '';
                },
            },
            {
                id: 'totalCost',
                header: 'Total Cost',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const totalCost = computePackagingItemTotalCost({
                        qty: getDraftValue(rowId, 'qty', row.original?.qty),
                        unitsPerCase: getDraftValue(rowId, 'unitsPerCase', row.original?.unitsPerCase),
                        unitPrice: getDraftValue(rowId, 'unitPrice', row.original?.unitPrice),
                    });
                    return totalCost != null ? formatPackagingCurrency(totalCost) : '';
                },
            },
            {
                id: 'remove',
                header: 'Remove',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const isNewRow = rowId.startsWith('new-');
                    return (
                        <Button
                            type="button"
                            variant={isRemoved ? 'destructive' : 'ghost'}
                            size="iconSm"
                            onClick={() => {
                                if (isNewRow && typeof onRemoveNewRow === 'function') {
                                    onRemoveNewRow(rowId);
                                    return;
                                }
                                toggleRemove(rowId);
                            }}
                            aria-label={isRemoved ? 'Unmark removal' : 'Mark for removal'}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    );
                },
            },
        ];

        return columns;
    }, [getDraftValue, onRemoveNewRow, setInputValue, toggleRemove]);

    const table = useReactTable({
        data: rows,
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(row.id),
    });

    return (
        <Table>
            <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <TableHead key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                        ))}
                    </TableRow>
                ))}
            </TableHeader>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    <TableRow
                        key={row.id}
                        className={
                            removedItemIdsRef.current?.has?.(String(row.original.id)) ? 'bg-muted/30' : undefined
                        }
                    >
                        {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className="text-sm">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

PackagingPurchaseOrderItemEditTable.displayName = 'PackagingPurchaseOrderItemEditTable';
